import type { FunctionComponent } from 'react'

import {
	Modal,
	ModalContent,
	ModalDescription,
	ModalFooter,
	ModalHeader,
	ModalTitle,
	ModalTitleContainer,
	ModalWrapper,
	MultipleChoice,
	PrimaryButton,
	TertiaryButton,
	TertiaryIconButton,
	useModal,
} from '@monthio/design-system'
import {
	Close,
	DanishFlag,
	DutchFlag,
	EnglishFlag,
	FinnishFlag,
	NorwegianFlag,
	SpanishFlag,
	SwedishFlag,
} from '@monthio/icons'
import { useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

export interface LocalizationSelectProps {
	languages: { languageCode: string; nativeName: string }[]
	onChange?: (langCode: string) => void
	value: string
}

export interface LocalizationSelectPropsModal {
	onChange?: (langCode: string) => void
	options: { languageCode: string; nativeName: string }[]
	value: string
}

export const getFlagIcon = (countryCode: string) => {
	switch (countryCode.toLowerCase()) {
		case 'da-dk':
		case 'dk':
			return DanishFlag
		case 'en-gb':
		case 'gb':
			return EnglishFlag
		case 'es':
		case 'es-es':
			return SpanishFlag
		case 'fi':
		case 'fi-fi':
			return FinnishFlag
		case 'nb-no':
		case 'no':
			return NorwegianFlag
		case 'nl':
		case 'nl-nl':
			return DutchFlag
		case 'se':
		case 'sv-se':
			return SwedishFlag
		default:
			return EnglishFlag
	}
}

const LanguageSwitchModal: FunctionComponent<LocalizationSelectPropsModal> = ({
	onChange,
	options,
	value,
}) => {
	const { close } = useModal()
	const { t } = useTranslation()
	const [selectedValue, setSelectedValue] = useState(value)
	const handleSave = useCallback(() => {
		close()
		onChange?.(selectedValue)
	}, [onChange, selectedValue, close])

	const optionsWithFlags = useMemo(() => {
		return options.map((option) => {
			const FlagIcon = getFlagIcon(option.languageCode)
			return {
				label: option.nativeName,
				startDecorator: <FlagIcon aria-hidden="true" />,
				value: option.languageCode,
			}
		})
	}, [options])

	return (
		<ModalWrapper>
			<ModalHeader>
				<TertiaryIconButton
					ariaLabel={t('btn-close')}
					icon={<Close aria-hidden="true" />}
					onClick={close}
					size="xl"
				/>
			</ModalHeader>
			<ModalContent>
				<MultipleChoice
					choices={optionsWithFlags}
					defaultValue={selectedValue}
					fieldName="lang"
					onChange={setSelectedValue}
					title={
						<ModalTitleContainer>
							<ModalTitle>
								{t('languageSwitch-title', { lng: selectedValue })}
							</ModalTitle>
							<ModalDescription>
								{t('languageSwitch-description', { lng: selectedValue })}
							</ModalDescription>
						</ModalTitleContainer>
					}
				/>
			</ModalContent>
			<ModalFooter>
				{/* this button is hidden from a screen reader,
        because we want to provide a single clear "Close" dialog button. */}
				<TertiaryButton aria-hidden="true" onClick={close} size="xl">
					{t('btn-close', { lng: selectedValue })}
				</TertiaryButton>
				<PrimaryButton
					aria-label={t('languageSwitch-saveBtn-ariaLabel', {
						lng: selectedValue,
					})}
					onClick={handleSave}
					size="xl"
				>
					{t('btn-save', { lng: selectedValue })}
				</PrimaryButton>
			</ModalFooter>
		</ModalWrapper>
	)
}

export const LanguageSwitchButton: FunctionComponent<
	LocalizationSelectProps
> = ({ languages, onChange, value }) => {
	const FlagIcon = getFlagIcon(value)
	const { t } = useTranslation()

	// Find the selected language option to get its nativeName
	const selectedOption = languages.find(
		(language) => language.languageCode === value,
	)
	const displayName = selectedOption?.nativeName || value

	return (
		<Modal
			trigger={
				<TertiaryIconButton
					ariaLabel={t('languageSwitch-triggerBtn-ariaLabel', {
						currentLanguage: displayName,
					})}
					icon={<FlagIcon aria-hidden="true" />}
					size="xl"
				/>
			}
		>
			<LanguageSwitchModal
				onChange={onChange}
				options={languages}
				value={value}
			/>
		</Modal>
	)
}
